import { BASE_URL, headers } from 'util/api/client';
import { createResourceLibrary } from 'active-resource';

export const APILibrary = createResourceLibrary(BASE_URL, {
  headers: headers(),
});

interface RelationshipClass {
  Klass: new () => unknown;
  options?: { polymorphic?: boolean; inverseOf?: string };
}

export const GenerateResource = <T>({
  queryName,
  className,
  AttributesClass,
  BelongsToClasses,
  HasManyClasses,
  HasOneClasses,
}: {
  queryName: string;
  className: string;
  AttributesClass: new () => T;
  BelongsToClasses?: RelationshipClass[];
  HasManyClasses?: RelationshipClass[];
  HasOneClasses?: RelationshipClass[];
}) =>
  class extends APILibrary.Base {
    static queryName = queryName;
    static className = className;

    constructor(initialAttributes?: Partial<T>) {
      super();
      Object.assign(this, initialAttributes);
    }

    static define() {
      this.attributes(...Object.keys(new AttributesClass()));

      if (BelongsToClasses) {
        BelongsToClasses.forEach(({ Klass, options }) => {
          Object.keys(new Klass()).forEach((key) => {
            this.belongsTo(key, options);
          });
        });
      }

      if (HasManyClasses) {
        HasManyClasses.forEach(({ Klass, options }) => {
          Object.keys(new Klass()).forEach((key) => {
            this.hasMany(key, options);
          });
        });
      }

      if (HasOneClasses) {
        HasOneClasses.forEach(({ Klass, options }) => {
          Object.keys(new Klass()).forEach((key) => {
            this.hasOne(key, options);
          });
        });
      }
    }
  };

export type QueryProps = {
  includes?: (Record<string, unknown> | string)[];
  select?: (Record<string, unknown> | string)[];
  where?: Record<string, unknown>;
  limit?: number;
  offset?: number;
  order?: Record<string, 'asc' | 'desc'>;
};

/**
 * This constructor type is the type of the generic class given by T.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type GConstructor<T = NonNullable<unknown>> = new (...args: any[]) => T;

/**\
 * The creator function is used as a reducer iterator.
 * @param acc The accumulator, this is the resulting generic class given by TBase.
 * @param child The child class, this is any generic mixin class given by TBase.
 */
const creator = <TBase>(
  acc: GConstructor<TBase>,
  child: (other: GConstructor<TBase>) => GConstructor<TBase>,
): GConstructor<TBase> => child(acc);

/**
 * This function takes in a list of class mixins and returns a new class that extends all of them.
 * @param classes A list of class mixins to extend.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ClassExtender = <TBase extends GConstructor<TBase>>(...classes: any[]) =>
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/ban-ts-comment
  // @ts-ignore
  class extends classes.reduce<TBase>(creator<TBase>, null) {
    constructor(args?: Partial<TBase>) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      super(args);
    }
  };

export default APILibrary;
